// src/components/invoices/InvoiceDetail.js
import React from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  Button,
  IconButton,
  Divider,
  Paper,
  Stack,
  useTheme,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  useMediaQuery,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import {
  ArrowBack,
  Edit,
  Send,
  PictureAsPdf,
  Person,
  CalendarToday,
  Payment,
  Receipt,
  Badge,
} from "@mui/icons-material";
import { useDocument } from "../../hooks/useDocument";
import { useCollection } from "../../hooks/useCollection";
import { sendInvoiceEmail } from "../../services/emailService";
import LoadingSpinner from "../ui/LoadingSpinner";
import EmptyState from "../ui/EmptyState";
import {
  PAYMENT_METHOD_LABELS,
  INVOICE_TYPE,
  LIQUIDATION_MODE,
} from "../../utils/models/invoiceModel";

const InvoiceDetail = ({ invoiceId, onEdit }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { document: invoice, loading } = useDocument("invoices", invoiceId);
  const { data: projects } = useCollection("projects");

  if (loading) return <LoadingSpinner />;
  if (!invoice) return <EmptyState title="Factura no encontrada" />;

  const isLiquidacion = invoice.invoiceType === INVOICE_TYPE.LIQUIDACION;
  const isLiquidationSwitch =
    isLiquidacion &&
    invoice.liquidationMode === LIQUIDATION_MODE.SWITCH;
  const assignToCompany = !!invoice.assignToCompany;

  const baseAmount = parseFloat(invoice.baseAmount) || 0;
  const taxAmount = parseFloat(invoice.taxAmount) || 0;
  const total = parseFloat(invoice.total) || 0;
  const taxRate = parseFloat(invoice.taxRate) || 0;

  const formatCurrency = (value) => {
    const num = parseFloat(value) || 0;
    return num.toFixed(2);
  };

  const formatDate = (value) => {
    if (!value) return "-";
    try {
      return new Date(value).toLocaleDateString("es-ES", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
    } catch {
      return "-";
    }
  };

  const statusColors = {
    unpaid: "warning",
    paid: "success",
    draft: "default",
    sent: "info",
    overdue: "error",
    cancelled: "error",
  };
  const statusLabels = {
    unpaid: "Sin pagar",
    paid: "Pagada",
    draft: "Borrador",
    sent: "Enviada",
    overdue: "Vencida",
    cancelled: "Cancelada",
  };

  // Datos efectivos a mostrar en el bloque del comercial
  const commercialDisplayName = assignToCompany
    ? invoice.commercialCompanyName || invoice.commercialName || "-"
    : invoice.commercialName || "-";

  const commercialDisplayTaxId = assignToCompany
    ? invoice.commercialCompanyTaxId || ""
    : invoice.commercialTaxId || "";

  const commercialDisplayAddress = assignToCompany
    ? invoice.commercialCompanyAddress || ""
    : invoice.commercialAddress || "";

  const commercialDisplayEmail = assignToCompany
    ? "" // sin email de empresa por ahora
    : invoice.commercialEmail || "";

  const handleSend = () => {
    const email = isLiquidacion
      ? invoice.commercialEmail
      : invoice.clientEmail;
    const name = isLiquidacion
      ? invoice.commercialName
      : invoice.clientName;
    if (email) sendInvoiceEmail(email, invoice.invoiceNumber, name);
  };

  const uniqueProjectIds = [
    ...new Set(
      (invoice.lines || []).filter((l) => l.projectId).map((l) => l.projectId),
    ),
  ];

  const getInvoiceTitle = () => {
    if (invoice.invoiceType === INVOICE_TYPE.PROFORMA)
      return "Factura Proforma";
    if (isLiquidationSwitch) return "Liquidación (Cambio de comercializadora)";
    if (invoice.invoiceType === INVOICE_TYPE.LIQUIDACION) return "Liquidación";
    if (invoice.invoiceType === INVOICE_TYPE.ABONO) return "Factura de Abono";
    return "Factura de Venta";
  };

  const getInvoiceChipLabel = () => {
    if (invoice.invoiceType === INVOICE_TYPE.PROFORMA) return "Proforma";
    if (isLiquidationSwitch) return "Liquidación · Cambio";
    if (invoice.invoiceType === INVOICE_TYPE.LIQUIDACION) return "Liquidación";
    if (invoice.invoiceType === INVOICE_TYPE.ABONO) return "Abono";
    return "Venta";
  };

  const getInvoiceChipColor = () => {
    if (invoice.invoiceType === INVOICE_TYPE.PROFORMA) return "warning";
    if (invoice.invoiceType === INVOICE_TYPE.LIQUIDACION) return "info";
    if (invoice.invoiceType === INVOICE_TYPE.ABONO) return "secondary";
    return "primary";
  };

  const getLinesTitle = () => {
    if (invoice.invoiceType === INVOICE_TYPE.ABONO) return "Líneas del abono";
    if (isLiquidationSwitch)
      return "Líneas de cambio de comercializadora";
    if (invoice.invoiceType === INVOICE_TYPE.LIQUIDACION)
      return "Líneas de la liquidación";
    return "Líneas de factura";
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 2, sm: 3, md: 4 },
        borderRadius: { xs: "16px", sm: "24px" },
        border: "1px solid",
        borderColor:
          theme.palette.mode === "dark" ? "rgba(255,255,255,0.12)" : "#e2e8f0",
        background: theme.palette.background.paper,
        overflow: "hidden",
      }}
    >
      {/* CABECERA */}
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          gap: { xs: 1, sm: 2 },
          mb: { xs: 3, sm: 4 },
          flexWrap: "wrap",
        }}
      >
        <IconButton
          onClick={() => navigate(-1)}
          size="small"
          sx={{
            background: theme.palette.background.paper,
            border: "1px solid",
            borderColor: theme.palette.divider,
            mt: 0.5,
          }}
        >
          <ArrowBack sx={{ fontSize: { xs: 20, sm: 24 } }} />
        </IconButton>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="h5"
            fontWeight={800}
            sx={{
              fontSize: { xs: "1.1rem", sm: "1.25rem", md: "1.5rem" },
              background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              wordBreak: "break-word",
            }}
          >
            {getInvoiceTitle()} {invoice.invoiceNumber || "BORRADOR"}
          </Typography>
          <Box sx={{ display: "flex", gap: 1, mt: 1, flexWrap: "wrap" }}>
            <Chip
              icon={<Receipt sx={{ fontSize: { xs: 14, sm: 16 } }} />}
              label={getInvoiceChipLabel()}
              size="small"
              color={getInvoiceChipColor()}
              variant="outlined"
              sx={{ fontSize: { xs: "0.65rem", sm: "0.75rem" } }}
            />
            <Chip
              label={statusLabels[invoice.status] || invoice.status}
              color={statusColors[invoice.status] || "default"}
              size="small"
              sx={{ fontSize: { xs: "0.65rem", sm: "0.75rem" } }}
            />
          </Box>
        </Box>

        <Stack direction="row" spacing={1} flexWrap="wrap">
          <Button
            startIcon={<Send />}
            variant="outlined"
            onClick={handleSend}
            size="small"
            sx={{
              borderRadius: "12px",
              textTransform: "none",
              fontWeight: 600,
              height: { xs: 36, sm: 44 },
              fontSize: { xs: "0.75rem", sm: "0.85rem" },
            }}
          >
            Enviar
          </Button>
          <Button
            startIcon={<PictureAsPdf />}
            variant="outlined"
            size="small"
            sx={{
              borderRadius: "12px",
              textTransform: "none",
              fontWeight: 600,
              height: { xs: 36, sm: 44 },
              fontSize: { xs: "0.75rem", sm: "0.85rem" },
            }}
          >
            PDF
          </Button>
          <Button
            startIcon={<Edit />}
            variant="contained"
            onClick={() => onEdit && onEdit(invoice)}
            size="small"
            sx={{
              borderRadius: "12px",
              textTransform: "none",
              fontWeight: 600,
              height: { xs: 36, sm: 44 },
              boxShadow: "none",
              fontSize: { xs: "0.75rem", sm: "0.85rem" },
            }}
          >
            Editar
          </Button>
        </Stack>
      </Box>

      {/* LÍNEAS */}
      {invoice.lines?.length > 0 && (
        <Card
          elevation={0}
          sx={{
            borderRadius: "16px",
            border: "1px solid",
            borderColor: theme.palette.divider,
            mb: 2,
          }}
        >
          <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
            <Typography
              variant="subtitle1"
              fontWeight={700}
              sx={{
                mb: 2,
                color: theme.palette.primary.main,
                fontSize: { xs: "0.9rem", sm: "1rem" },
              }}
            >
              {getLinesTitle()}
            </Typography>
            <TableContainer
              sx={{
                borderRadius: "12px",
                border: "1px solid",
                borderColor: theme.palette.divider,
              }}
            >
              <Table size={isMobile ? "small" : "medium"}>
                <TableHead>
                  <TableRow
                    sx={{
                      backgroundColor:
                        theme.palette.mode === "dark"
                          ? "rgba(0, 175, 163, 0.1)"
                          : "rgba(0, 95, 101, 0.05)",
                    }}
                  >
                    {isLiquidationSwitch ? (
                      <>
                        <TableCell
                          sx={{
                            fontWeight: 600,
                            fontSize: { xs: "0.7rem", sm: "0.8rem" },
                          }}
                        >
                          Cliente
                        </TableCell>
                        <TableCell
                          sx={{
                            fontWeight: 600,
                            fontSize: { xs: "0.7rem", sm: "0.8rem" },
                          }}
                        >
                          CUPS
                        </TableCell>
                        <TableCell
                          sx={{
                            fontWeight: 600,
                            fontSize: { xs: "0.7rem", sm: "0.8rem" },
                          }}
                        >
                          Fecha entrada
                        </TableCell>
                        <TableCell
                          sx={{
                            fontWeight: 600,
                            fontSize: { xs: "0.7rem", sm: "0.8rem" },
                          }}
                        >
                          Comisión sin IVA
                        </TableCell>
                      </>
                    ) : isLiquidacion ? (
                      <>
                        <TableCell
                          sx={{
                            fontWeight: 600,
                            fontSize: { xs: "0.7rem", sm: "0.8rem" },
                          }}
                        >
                          Proyecto
                        </TableCell>
                        <TableCell
                          sx={{
                            fontWeight: 600,
                            fontSize: { xs: "0.7rem", sm: "0.8rem" },
                          }}
                        >
                          Trimestre
                        </TableCell>
                        <TableCell
                          sx={{
                            fontWeight: 600,
                            fontSize: { xs: "0.7rem", sm: "0.8rem" },
                          }}
                        >
                          Comisión sin IVA
                        </TableCell>
                      </>
                    ) : (
                      <>
                        <TableCell
                          sx={{
                            fontWeight: 600,
                            fontSize: { xs: "0.7rem", sm: "0.8rem" },
                          }}
                        >
                          Concepto
                        </TableCell>
                        <TableCell
                          sx={{
                            fontWeight: 600,
                            fontSize: { xs: "0.7rem", sm: "0.8rem" },
                          }}
                        >
                          Cant.
                        </TableCell>
                        <TableCell
                          sx={{
                            fontWeight: 600,
                            fontSize: { xs: "0.7rem", sm: "0.8rem" },
                          }}
                        >
                          Precio ud.
                        </TableCell>
                        <TableCell
                          sx={{
                            fontWeight: 600,
                            fontSize: { xs: "0.7rem", sm: "0.8rem" },
                          }}
                        >
                          IVA
                        </TableCell>
                        <TableCell
                          sx={{
                            fontWeight: 600,
                            fontSize: { xs: "0.7rem", sm: "0.8rem" },
                          }}
                        >
                          Total
                        </TableCell>
                      </>
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {invoice.lines.map((line) => {
                    if (isLiquidationSwitch) {
                      return (
                        <TableRow key={line.id}>
                          <TableCell>
                            <Typography
                              variant="body2"
                              sx={{ fontSize: { xs: "0.7rem", sm: "0.85rem" } }}
                            >
                              {line.switchClientName || "-"}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography
                              variant="body2"
                              sx={{
                                fontSize: { xs: "0.7rem", sm: "0.85rem" },
                                wordBreak: "break-all",
                              }}
                            >
                              {line.cups || "-"}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography
                              variant="body2"
                              sx={{ fontSize: { xs: "0.7rem", sm: "0.85rem" } }}
                            >
                              {formatDate(line.fechaEntrada)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography
                              fontWeight={600}
                              sx={{ fontSize: { xs: "0.7rem", sm: "0.85rem" } }}
                            >
                              {formatCurrency(line.commission)}€
                            </Typography>
                          </TableCell>
                        </TableRow>
                      );
                    }

                    if (isLiquidacion) {
                      return (
                        <TableRow key={line.id}>
                          <TableCell>
                            <Typography
                              variant="body2"
                              sx={{ fontSize: { xs: "0.7rem", sm: "0.85rem" } }}
                            >
                              {line.projectName ||
                                projects?.find((p) => p.id === line.projectId)
                                  ?.title ||
                                projects?.find((p) => p.id === line.projectId)
                                  ?.name ||
                                "-"}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography
                              variant="body2"
                              sx={{ fontSize: { xs: "0.7rem", sm: "0.85rem" } }}
                            >
                              {line.quarter || "-"}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography
                              fontWeight={600}
                              sx={{ fontSize: { xs: "0.7rem", sm: "0.85rem" } }}
                            >
                              {formatCurrency(line.commission)}€
                            </Typography>
                          </TableCell>
                        </TableRow>
                      );
                    }

                    return (
                      <TableRow key={line.id}>
                        <TableCell>
                          <Typography
                            variant="body2"
                            sx={{ fontSize: { xs: "0.7rem", sm: "0.85rem" } }}
                          >
                            {line.description || "-"}
                          </Typography>
                          {line.projectId && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ fontSize: { xs: "0.6rem", sm: "0.7rem" } }}
                            >
                              Proyecto:{" "}
                              {projects?.find((p) => p.id === line.projectId)
                                ?.title ||
                                projects?.find((p) => p.id === line.projectId)
                                  ?.name ||
                                line.projectId}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography
                            variant="body2"
                            sx={{ fontSize: { xs: "0.7rem", sm: "0.85rem" } }}
                          >
                            {line.quantity}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography
                            variant="body2"
                            sx={{ fontSize: { xs: "0.7rem", sm: "0.85rem" } }}
                          >
                            {formatCurrency(line.unitPrice)}€
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography
                            variant="body2"
                            sx={{ fontSize: { xs: "0.7rem", sm: "0.85rem" } }}
                          >
                            {parseFloat(line.taxRate) || 0}%
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography
                            fontWeight={600}
                            sx={{ fontSize: { xs: "0.7rem", sm: "0.85rem" } }}
                          >
                            {formatCurrency(line.total)}€
                          </Typography>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {/* CONTENIDO PRINCIPAL */}
      <Box
        sx={{
          display: "flex",
          gap: { xs: 2, sm: 3 },
          flexWrap: "wrap",
          width: "100%",
          flexDirection: { xs: "column", md: "row-reverse" },
        }}
      >
        {/* IMPORTES + PROYECTOS */}
        <Box
          sx={{
            flex: { md: "2 1 540px" },
            width: { xs: "100%", md: "auto" },
            minWidth: { md: "350px" },
          }}
        >
          <Card
            elevation={0}
            sx={{
              borderRadius: "16px",
              border: "1px solid",
              borderColor: theme.palette.divider,
              mb: 2,
            }}
          >
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
              <Typography
                variant="subtitle1"
                fontWeight={700}
                sx={{
                  mb: 2,
                  color: theme.palette.primary.main,
                  fontSize: { xs: "0.9rem", sm: "1rem" },
                }}
              >
                Importes
              </Typography>
              <Box
                sx={{
                  p: 2,
                  borderRadius: "16px",
                  border: "1px solid",
                  borderColor: theme.palette.divider,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mb: 1,
                  }}
                >
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ fontSize: { xs: "0.8rem", sm: "0.85rem" } }}
                  >
                    {isLiquidacion ? "Comisión sin IVA" : "Base imponible"}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ fontSize: { xs: "0.8rem", sm: "0.85rem" } }}
                  >
                    {formatCurrency(baseAmount)}€
                  </Typography>
                </Box>

                {!isLiquidacion && (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      mb: 1,
                    }}
                  >
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ fontSize: { xs: "0.8rem", sm: "0.85rem" } }}
                    >
                      IVA ({taxRate}%)
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ fontSize: { xs: "0.8rem", sm: "0.85rem" } }}
                    >
                      {formatCurrency(taxAmount)}€
                    </Typography>
                  </Box>
                )}

                <Divider sx={{ my: 1 }} />
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography
                    variant="body2"
                    fontWeight={700}
                    sx={{ fontSize: { xs: "0.85rem", sm: "0.9rem" } }}
                  >
                    Total
                  </Typography>
                  <Typography
                    variant="h6"
                    fontWeight={700}
                    color={theme.palette.primary.main}
                    sx={{ fontSize: { xs: "1rem", sm: "1.25rem" } }}
                  >
                    {formatCurrency(total)}€
                  </Typography>
                </Box>
              </Box>

              {!isLiquidationSwitch && uniqueProjectIds.length > 0 && (
                <>
                  <Typography
                    variant="subtitle2"
                    fontWeight={700}
                    sx={{
                      mt: 2,
                      mb: 1,
                      fontSize: { xs: "0.8rem", sm: "0.85rem" },
                    }}
                  >
                    Proyectos asociados
                  </Typography>
                  {uniqueProjectIds.map((projectId) => {
                    const project = projects?.find((p) => p.id === projectId);
                    const projectLines = invoice.lines.filter(
                      (l) => l.projectId === projectId,
                    );
                    const projectTotal = projectLines.reduce((sum, l) => {
                      if (isLiquidacion) {
                        return sum + (parseFloat(l.commission) || 0);
                      }
                      return (
                        sum +
                        (parseFloat(l.total) || 0) +
                        (parseFloat(l.taxAmount) || 0)
                      );
                    }, 0);
                    return (
                      <Box
                        key={projectId}
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          mb: 0.5,
                          p: 1,
                          borderRadius: "8px",
                          bgcolor: theme.palette.background.default,
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{ fontSize: { xs: "0.75rem", sm: "0.85rem" } }}
                        >
                          {project?.title || project?.name || "Proyecto"}
                        </Typography>
                        <Typography
                          variant="body2"
                          fontWeight={600}
                          sx={{ fontSize: { xs: "0.75rem", sm: "0.85rem" } }}
                        >
                          {projectTotal.toFixed(2)}€
                        </Typography>
                      </Box>
                    );
                  })}
                </>
              )}

              {isLiquidationSwitch &&
                Array.isArray(invoice.lines) &&
                invoice.lines.length > 0 && (
                  <>
                    <Typography
                      variant="subtitle2"
                      fontWeight={700}
                      sx={{
                        mt: 2,
                        mb: 1,
                        fontSize: { xs: "0.8rem", sm: "0.85rem" },
                      }}
                    >
                      Resumen por CUPS
                    </Typography>
                    {invoice.lines.map((line) => (
                      <Box
                        key={line.id}
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          gap: 1,
                          mb: 0.5,
                          p: 1,
                          borderRadius: "8px",
                          bgcolor: theme.palette.background.default,
                        }}
                      >
                        <Box sx={{ minWidth: 0 }}>
                          <Typography
                            variant="body2"
                            sx={{
                              fontSize: { xs: "0.75rem", sm: "0.85rem" },
                              fontWeight: 600,
                            }}
                            noWrap
                          >
                            {line.switchClientName || "-"}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{
                              fontSize: { xs: "0.65rem", sm: "0.7rem" },
                              wordBreak: "break-all",
                            }}
                          >
                            {line.cups || "-"} · {formatDate(line.fechaEntrada)}
                          </Typography>
                        </Box>
                        <Typography
                          variant="body2"
                          fontWeight={600}
                          sx={{ fontSize: { xs: "0.75rem", sm: "0.85rem" } }}
                        >
                          {formatCurrency(line.commission)}€
                        </Typography>
                      </Box>
                    ))}
                  </>
                )}
            </CardContent>
          </Card>

          {invoice.notes && (
            <Card
              elevation={0}
              sx={{
                borderRadius: "16px",
                border: "1px solid",
                borderColor: theme.palette.divider,
                mb: 2,
              }}
            >
              <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                <Typography
                  variant="subtitle1"
                  fontWeight={700}
                  sx={{
                    mb: 2,
                    color: theme.palette.primary.main,
                    fontSize: { xs: "0.9rem", sm: "1rem" },
                  }}
                >
                  Notas
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: { xs: "0.8rem", sm: "0.85rem" },
                    whiteSpace: "pre-wrap",
                  }}
                >
                  {invoice.notes}
                </Typography>
              </CardContent>
            </Card>
          )}
        </Box>

        {/* INFORMACIÓN */}
        <Box
          sx={{
            flex: { md: "1 1 340px" },
            width: { xs: "100%", md: "auto" },
            minWidth: { md: "300px" },
          }}
        >
          {/* Emisor */}
          <Card
            elevation={0}
            sx={{
              borderRadius: "16px",
              border: "1px solid",
              borderColor: theme.palette.divider,
              mb: 2,
            }}
          >
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
              <Typography
                variant="subtitle1"
                fontWeight={700}
                sx={{
                  mb: 2,
                  color: theme.palette.primary.main,
                  fontSize: { xs: "0.9rem", sm: "1rem" },
                }}
              >
                Emisor (EPC)
              </Typography>
              <List dense disablePadding>
                <ListItem sx={{ px: 0 }}>
                  <ListItemText
                    primary={
                      invoice.issuerName || "EPC Development & Management, S.L."
                    }
                    secondary={invoice.issuerTaxId || "B26623850"}
                    primaryTypographyProps={{
                      variant: "body2",
                      fontWeight: 600,
                      fontSize: { xs: "0.8rem", sm: "0.85rem" },
                    }}
                    secondaryTypographyProps={{
                      variant: "caption",
                      fontSize: { xs: "0.7rem", sm: "0.75rem" },
                    }}
                  />
                </ListItem>
                {invoice.issuerAddress && (
                  <ListItem sx={{ px: 0 }}>
                    <ListItemText
                      primary="Dirección"
                      secondary={invoice.issuerAddress}
                      primaryTypographyProps={{
                        variant: "caption",
                        color: "text.secondary",
                        fontSize: { xs: "0.7rem", sm: "0.75rem" },
                      }}
                      secondaryTypographyProps={{
                        variant: "body2",
                        fontSize: { xs: "0.75rem", sm: "0.85rem" },
                      }}
                    />
                  </ListItem>
                )}
              </List>
            </CardContent>
          </Card>

          {/* Cliente o Comercial */}
          <Card
            elevation={0}
            sx={{
              borderRadius: "16px",
              border: "1px solid",
              borderColor: theme.palette.divider,
              mb: 2,
            }}
          >
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
              <Typography
                variant="subtitle1"
                fontWeight={700}
                sx={{
                  mb: 2,
                  color: theme.palette.primary.main,
                  fontSize: { xs: "0.9rem", sm: "1rem" },
                }}
              >
                {isLiquidacion
                  ? assignToCompany
                    ? "Comercial (Empresa)"
                    : "Comercial"
                  : invoice.invoiceType === INVOICE_TYPE.ABONO
                    ? "Cliente (receptor del abono)"
                    : "Cliente"}
              </Typography>
              <List dense disablePadding>
                <ListItem sx={{ px: 0 }}>
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    {isLiquidacion ? (
                      <Badge
                        sx={{
                          fontSize: { xs: 18, sm: 20 },
                          color: theme.palette.primary.main,
                        }}
                      />
                    ) : (
                      <Person
                        sx={{
                          fontSize: { xs: 18, sm: 20 },
                          color: theme.palette.primary.main,
                        }}
                      />
                    )}
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      isLiquidacion
                        ? commercialDisplayName
                        : invoice.clientName || "-"
                    }
                    secondary={
                      isLiquidacion
                        ? commercialDisplayEmail ||
                          commercialDisplayTaxId ||
                          ""
                        : invoice.clientEmail || invoice.clientTaxId || ""
                    }
                    primaryTypographyProps={{
                      variant: "body2",
                      fontWeight: 600,
                      fontSize: { xs: "0.8rem", sm: "0.85rem" },
                    }}
                    secondaryTypographyProps={{
                      variant: "caption",
                      fontSize: { xs: "0.7rem", sm: "0.75rem" },
                      wordBreak: "break-all",
                    }}
                  />
                </ListItem>
                {(isLiquidacion
                  ? commercialDisplayAddress
                  : invoice.clientAddress) && (
                  <ListItem sx={{ px: 0 }}>
                    <ListItemText
                      primary="Dirección"
                      secondary={
                        isLiquidacion
                          ? commercialDisplayAddress
                          : invoice.clientAddress
                      }
                      primaryTypographyProps={{
                        variant: "caption",
                        color: "text.secondary",
                        fontSize: { xs: "0.7rem", sm: "0.75rem" },
                      }}
                      secondaryTypographyProps={{
                        variant: "body2",
                        fontSize: { xs: "0.75rem", sm: "0.85rem" },
                      }}
                    />
                  </ListItem>
                )}
              </List>
            </CardContent>
          </Card>

          {/* Fechas */}
          <Card
            elevation={0}
            sx={{
              borderRadius: "16px",
              border: "1px solid",
              borderColor: theme.palette.divider,
              mb: 2,
            }}
          >
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
              <Typography
                variant="subtitle1"
                fontWeight={700}
                sx={{
                  mb: 2,
                  color: theme.palette.primary.main,
                  fontSize: { xs: "0.9rem", sm: "1rem" },
                }}
              >
                Fechas
              </Typography>
              <List dense disablePadding>
                <ListItem sx={{ px: 0 }}>
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <CalendarToday
                      sx={{
                        fontSize: { xs: 18, sm: 20 },
                        color: theme.palette.primary.main,
                      }}
                    />
                  </ListItemIcon>
                  <ListItemText
                    primary="Emisión"
                    secondary={
                      invoice.issueDate
                        ? new Date(invoice.issueDate).toLocaleDateString()
                        : "-"
                    }
                    primaryTypographyProps={{
                      variant: "caption",
                      color: "text.secondary",
                      fontSize: { xs: "0.7rem", sm: "0.75rem" },
                    }}
                    secondaryTypographyProps={{
                      variant: "body2",
                      fontSize: { xs: "0.8rem", sm: "0.85rem" },
                    }}
                  />
                </ListItem>
                <ListItem sx={{ px: 0 }}>
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <CalendarToday
                      sx={{
                        fontSize: { xs: 18, sm: 20 },
                        color: theme.palette.warning.main,
                      }}
                    />
                  </ListItemIcon>
                  <ListItemText
                    primary="Vencimiento"
                    secondary={
                      invoice.dueDate
                        ? new Date(invoice.dueDate).toLocaleDateString()
                        : "-"
                    }
                    primaryTypographyProps={{
                      variant: "caption",
                      color: "text.secondary",
                      fontSize: { xs: "0.7rem", sm: "0.75rem" },
                    }}
                    secondaryTypographyProps={{
                      variant: "body2",
                      fontSize: { xs: "0.8rem", sm: "0.85rem" },
                    }}
                  />
                </ListItem>
                {invoice.paidAt && (
                  <ListItem sx={{ px: 0 }}>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <CalendarToday
                        sx={{
                          fontSize: { xs: 18, sm: 20 },
                          color: theme.palette.success.main,
                        }}
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary="Pagada"
                      secondary={new Date(invoice.paidAt).toLocaleDateString()}
                      primaryTypographyProps={{
                        variant: "caption",
                        color: "text.secondary",
                        fontSize: { xs: "0.7rem", sm: "0.75rem" },
                      }}
                      secondaryTypographyProps={{
                        variant: "body2",
                        fontSize: { xs: "0.8rem", sm: "0.85rem" },
                      }}
                    />
                  </ListItem>
                )}
              </List>
            </CardContent>
          </Card>

          {/* Cobro — OCULTO EN ABONOS Y LIQUIDACIONES */}
          {invoice.paymentMethod &&
            invoice.invoiceType !== INVOICE_TYPE.ABONO &&
            invoice.invoiceType !== INVOICE_TYPE.LIQUIDACION && (
              <Card
                elevation={0}
                sx={{
                  borderRadius: "16px",
                  border: "1px solid",
                  borderColor: theme.palette.divider,
                  mb: 2,
                }}
              >
                <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                  <Typography
                    variant="subtitle1"
                    fontWeight={700}
                    sx={{
                      mb: 2,
                      color: theme.palette.primary.main,
                      fontSize: { xs: "0.9rem", sm: "1rem" },
                    }}
                  >
                    Cobro
                  </Typography>
                  <List dense disablePadding>
                    <ListItem sx={{ px: 0 }}>
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <Payment
                          sx={{
                            fontSize: { xs: 18, sm: 20 },
                            color: theme.palette.success.main,
                          }}
                        />
                      </ListItemIcon>
                      <ListItemText
                        primary="Método"
                        secondary={
                          PAYMENT_METHOD_LABELS[invoice.paymentMethod] ||
                          invoice.paymentMethod
                        }
                        primaryTypographyProps={{
                          variant: "caption",
                          color: "text.secondary",
                          fontSize: { xs: "0.7rem", sm: "0.75rem" },
                        }}
                        secondaryTypographyProps={{
                          variant: "body2",
                          fontSize: { xs: "0.8rem", sm: "0.85rem" },
                        }}
                      />
                    </ListItem>
                    {invoice.iban && (
                      <ListItem sx={{ px: 0 }}>
                        <ListItemText
                          primary="IBAN"
                          secondary={invoice.iban}
                          primaryTypographyProps={{
                            variant: "caption",
                            color: "text.secondary",
                            fontSize: { xs: "0.7rem", sm: "0.75rem" },
                          }}
                          secondaryTypographyProps={{
                            variant: "body2",
                            fontSize: { xs: "0.8rem", sm: "0.85rem" },
                            wordBreak: "break-all",
                          }}
                        />
                      </ListItem>
                    )}
                  </List>
                </CardContent>
              </Card>
            )}
        </Box>
      </Box>
    </Paper>
  );
};

export default InvoiceDetail;